import { Box, Button, Card, CardContent, CircularProgress, Grid, Paper } from '@material-ui/core';
import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/core/styles';
import Alert from '@material-ui/lab/Alert';
import axios from 'axios';
import { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';

import { CaptureSignature } from '../../../private/components/CaptureSignature/capture-signature';
import { ApplicationFormContainer } from '../../../private/pages/ApplicationsForm/applications-form.styles';
import { CmsArticles, fetchCmsArticle } from '../../../shared/helpers/cms-articles';

export interface IEmailGetSignatureData {
  childFirstName: string;
  childLastName: string;
  customMessage: string;
  legalText: string;
  signeeFirstName: string;
  signeeLastName: string;
  sk: string;
  userId: string;
}

export interface IEmailSaveSignatureData {
  userId: string;
  sk: string;
  signed: boolean;
  signedAgreementWording: string;
  firstName: string;
  lastName: string;
}

const useStyles = makeStyles((theme) => ({
  section: {
    marginLeft: '12px',
    marginRight: '12px'
  },
  title: {
    marginLeft: '12px',
    marginRight: '12px'
  },
  [theme.breakpoints.down('xs')]: {
    section: {
      marginLeft: 0,
      marginRight: 0
    },
    title: {
      marginLeft: '12px',
      marginRight: '12px'
    }
  }
}));

const PublicMentionAccept = (): JSX.Element => {
  const [data, setData] = useState<IEmailGetSignatureData | undefined>(undefined);
  const [dataError, setDataError] = useState('');
  const [isFetching, setIsFetching] = useState(false);
  const [notChecked, setNotChecked] = useState(true);
  const [isComplete, setIsComplete] = useState(false);
  const [purchaserLegalText, setPurchaserLegalText] = useState('');
  const { search } = useLocation();
  const searchParams = new URLSearchParams(search);
  const encodedData = searchParams.get('request');
  const classes = useStyles();

  if (!encodedData) {
    return <Navigate to="/" replace />;
  }

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsFetching(true);
        const response = await axios.post(`${process.env.REACT_APP_SERVICE_MTPF_URL}/v1/decode`, {
          signatureCode: decodeURI(encodedData)
        });
        setData(response.data.data);
        setIsFetching(false);
      } catch (e) {
        console.error(e);
        setDataError('Unable to find request. Please contact us if you believe this is a mistake.');
        setIsFetching(false);
      }
    };
    if (!data) {
      void fetchData();
    }
  }, []);

  useEffect(() => {
    const fetchArticle = async () => {
      try {
        const purchaserLegalTextHTML = await fetchCmsArticle(CmsArticles.PURCHASER_LEGAL_TEXT);
        setPurchaserLegalText(purchaserLegalTextHTML);
      } catch (e) {
        console.error(e);
      }
    };

    void fetchArticle();
  }, []);

  const changeCallback = async (value: { checked: boolean; firstName: string; lastName: string }): Promise<void> => {
    setNotChecked(!value.checked);
    setData((prevState) => {
      if (!prevState) {
        return prevState;
      }

      return {
        ...prevState,
        signeeFirstName: value.firstName,
        signeeLastName: value.lastName
      };
    });
  };

  const handleSubmit = async (): Promise<void> => {
    try {
      if (!data) {
        setDataError('Missing signature request data.');

        return;
      }

      const saveSignatureData: IEmailSaveSignatureData = {
        sk: data.sk,
        userId: data.userId,
        signed: true,
        signedAgreementWording: purchaserLegalText,
        firstName: data.signeeFirstName,
        lastName: data.signeeLastName
      };
      setIsFetching(true);
      await axios.post(`${process.env.REACT_APP_SERVICE_MTPF_URL}/v1/save-signature`, saveSignatureData);
      setIsFetching(false);
      setIsComplete(true);
    } catch (e) {
      console.error(e);
      setDataError('Unable to save signature.');
      setIsFetching(false);
    }
  };

  const renderSignatureRequest = (): JSX.Element => (
    <Card style={{ marginLeft: '12px', marginRight: '12px' }}>
      <CardContent className={classes.section}>
        <Grid container justifyContent="space-between" spacing={3}>
          {data?.legalText && (
            <Grid item xs={12}>
              <div className="lead" dangerouslySetInnerHTML={{ __html: data.legalText }} />
            </Grid>
          )}
          {data?.customMessage && (
            <Grid item xs={12}>
              <strong>Note from requestor: </strong>
              <Paper elevation={2} style={{ padding: 12 }}>
                {data.customMessage}
              </Paper>
            </Grid>
          )}
          <Grid item xs={12}>
            <CaptureSignature
              signedAgreementWording={purchaserLegalText}
              parentLabel="Your information"
              childLabel="Student information"
              initialFirstName={data!.signeeFirstName}
              initialLastName={data!.signeeLastName}
              initialChildFirstName={data!.childFirstName}
              initialChildLastName={data!.childLastName}
              initialChecked={false}
              initialSignatureType="purchaser"
              initialSignatureFormat="digital"
              changeCallback={changeCallback}
            />
          </Grid>
          <Grid item xs={12}>
            <Box textAlign={'center'}>
              <Button variant="contained" color="primary" onClick={handleSubmit} disabled={notChecked}>
                Confirm Identity
                {isFetching && (
                  <Box ml={2} mt={1}>
                    <CircularProgress size={18} color={'inherit'} />
                  </Box>
                )}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );

  return (
    <ApplicationFormContainer>
      <Grid container spacing={2}>
        <Grid item xs={12} className={classes.title}>
          {dataError && <Alert severity="error">{dataError}</Alert>}
          <Typography variant="h6" gutterBottom>
            Acknowledgment Request
          </Typography>
          {isComplete && <h4>Thank you.</h4>}
          {isFetching && (
            <Box m="auto">
              <CircularProgress />
            </Box>
          )}
        </Grid>
        <Grid item xs={12}>
          {!isFetching && !dataError && !isComplete && data && renderSignatureRequest()}
        </Grid>
      </Grid>
    </ApplicationFormContainer>
  );
};

export default PublicMentionAccept;
